/**
 * use-skeleton-loading.ts
 * 
 * Custom hook for managing loading states and skeleton screens
 * Provides a standardized way to handle loading states across components
 * Implements a minimum display time for skeleton loaders to prevent flickering
 */

import { useState, useEffect } from 'react';

interface UseSkeletonLoadingOptions {
  /**
   * Minimum time to show the skeleton loader in milliseconds
   * This prevents flickering if data loads too quickly
   */
  minDisplayTime?: number;
  
  /**
   * Initial loading state
   */
  initialLoading?: boolean;
}

/**
 * useSkeletonLoading hook for managing skeleton loader visibility
 * 
 * Features:
 * - Ensures minimum display time to prevent UI flicker
 * - Handles showing/hiding skeleton loaders with smooth transitions
 * - Provides loading state for component integration
 * 
 * @param options Configuration options for the skeleton loading behavior
 * @returns Object containing loading state and functions to control it
 */
export function useSkeletonLoading({
  minDisplayTime = 500,
  initialLoading = false
}: UseSkeletonLoadingOptions = {}) {
  const [isLoading, setIsLoading] = useState(initialLoading);
  const [startTime, setStartTime] = useState<number | null>(initialLoading ? Date.now() : null);
  
  // Start loading and record start time
  const startLoading = () => {
    setIsLoading(true);
    setStartTime(Date.now());
  };
  
  // End loading, respecting minimum display time
  const endLoading = () => {
    if (!startTime) {
      setIsLoading(false);
      return;
    }
    
    const elapsedTime = Date.now() - startTime;
    
    if (elapsedTime >= minDisplayTime) {
      // If minimum time elapsed, hide immediately
      setIsLoading(false);
      setStartTime(null);
    } else {
      // Otherwise, wait for remaining time
      const remainingTime = minDisplayTime - elapsedTime;
      setTimeout(() => {
        setIsLoading(false);
        setStartTime(null);
      }, remainingTime);
    }
  };
  
  return {
    isLoading,
    startLoading,
    endLoading,
    setLoading: setIsLoading
  };
}

export default useSkeletonLoading;
